/**
 * Figure frames kept in this browser: every frame the workers draw (workers.ts) is packed into
 * atlas pages and stored in IndexedDB (cache/store.ts, under `frames/`), and the next load cuts
 * them from there instead of drawing them again. Drawing the figures is the bulk of the loading
 * screen (thousands of frames); a reload or a zone change then takes a fraction of the time.
 *
 * The frames are exactly what the workers would draw: they are kept under a stamp of everything a
 * frame is drawn from (`virtual:frames-hash`, the drawing code and the shared looks) and the page's
 * pixel density, and a stamp that no longer matches drops them. Kept within the player's cache cap
 * (cache.setting.ts) like any other file, and cleared with it.
 *
 * Every step fails soft: no IndexedDB, a page that will not decode or store, and the frames are
 * drawn as before.
 */
import FRAMES_HASH from 'virtual:frames-hash';
import { assetStore, MADE_HERE } from '../../cache/store';
import { density } from '../density';
import { figureOutlineTag } from '../figureOutlineFlag';
import { truePxFigures } from '../truepx/flags';
import { artX, artY, atRes, canvas, ctx2d, resOf } from '../pixel';
import type { Pose, View8 as View } from '../sprites';
import { frameKey } from './bake';
import type { BladeLine } from './weapons';
import { type FrameRequest, whenFrameDrawn } from './workers';

/** Where the pages are kept in the asset store (files made in this browser, never pruned by a manifest). */
const FRAMES_PREFIX = MADE_HERE;
/** A page's width and greatest height (canvas pixels). */
const PAGE = 2048;
/** Pages kept for one stamp at most: past it, frames are drawn as before but not kept. */
const MAX_PAGES = 96;
/** A part-filled page is stored once no new frame has come for this long. */
const FLUSH_MS = 1500;
/** The loaded pages are let go this long after the loading scene lifts (`releaseSoon`). */
const RELEASE_MS = 20_000;

/** A frame in its page: where, its canvas pixels per art pixel, and its top-left in the full frame. */
type Rect = [x: number, y: number, w: number, h: number, res: number, ax: number, ay: number];
/** A page's description, stored beside its PNG. */
interface PageMeta {
  stamp: string;
  looks: string[];
  /** [look index, frame key, ...rect]. */
  frames: [number, string, ...Rect][];
  /** Weapon lines, by index into `frames`. */
  blades?: Record<number, BladeLine[]>;
}

/** This page's stamp: what frames are drawn from, and at what density. */
const stamp = (): string => `${FRAMES_HASH}-${density()}${truePxFigures() ? '-truepx' : ''}${figureOutlineTag()}`;
const pathOf = (id: string, ext: 'png' | 'json'): string => `${FRAMES_PREFIX}${id}.${ext}`;
const nameOf = (look: string, key: string): string => `${look}\n${key}`;

/** Frames loaded from the store, by look and frame key. */
const found = new Map<string, { img: ImageBitmap; r: Rect; blades: BladeLine[] }>();
const pages: ImageBitmap[] = [];
/** How the cache did this load (read in dev tools, `__frameCache`). */
export const frameCacheStats = { pages: 0, frames: 0, bytes: 0, loadMs: 0, hits: 0, kept: 0, stored: 0, failed: 0 };

let loading: Promise<void> | null = null;
let ready = false;
let released = false;

/**
 * Loads the frames kept for this stamp (once; call after the density is set). Never rejects: with
 * nothing kept, or no IndexedDB, it resolves with nothing found.
 */
export function loadFrameCache(): Promise<void> {
  loading ??= (async () => {
    const t0 = performance.now();
    try {
      if (typeof document === 'undefined') return;
      if (!(await assetStore.open())) return;
      const now = stamp();
      const mine = assetStore.entries().filter((m) => m.path.startsWith(FRAMES_PREFIX));
      // Frames of other code or density: dropped, in the background.
      void assetStore.remove(mine.filter((m) => m.hash !== now).map((m) => m.path));
      frameCacheStats.bytes = mine.filter((m) => m.hash === now).reduce((a, m) => a + m.size, 0);
      const ids = mine
        .filter((m) => m.hash === now && m.path.endsWith('.json'))
        .map((m) => m.path.slice(FRAMES_PREFIX.length, -'.json'.length));
      await Promise.all(ids.map((id) => loadPage(id, now)));
      frameCacheStats.pages = pages.length;
      frameCacheStats.frames = found.size;
    } catch (error) {
      console.warn('Frame cache: not loaded, drawing figures instead', error);
    } finally {
      ready = true;
      frameCacheStats.loadMs = Math.round(performance.now() - t0);
      startKeeping();
    }
  })();
  return loading;
}

async function loadPage(id: string, now: string): Promise<void> {
  const [json, png] = await Promise.all([
    assetStore.read(pathOf(id, 'json'), now),
    assetStore.read(pathOf(id, 'png'), now),
  ]);
  if (!json || !png) {
    void assetStore.remove([pathOf(id, 'json'), pathOf(id, 'png')]);
    return;
  }
  try {
    const meta = JSON.parse(await json.text()) as PageMeta;
    if (meta.stamp !== now) return;
    const img = await createImageBitmap(png, { premultiplyAlpha: 'premultiply' });
    pages.push(img);
    meta.frames.forEach(([look, key, ...r], i) => {
      const lookKey = meta.looks[look];
      if (lookKey !== undefined) found.set(nameOf(lookKey, key), { img, r, blades: meta.blades?.[i] ?? [] });
    });
  } catch {
    void assetStore.remove([pathOf(id, 'json'), pathOf(id, 'png')]);
  }
}

/**
 * A frame of a look kept in this browser, cut from its page, if it is there (sets.ts asks before
 * giving the frame to a worker).
 */
export function cachedFrame(
  look: string,
  view: View,
  pose: Pose,
  ph: number,
  lag: number,
  blink: boolean,
): { c: HTMLCanvasElement; blades: BladeLine[] } | undefined {
  if (!found.size) return undefined;
  const got = found.get(nameOf(look, frameKey(view, pose, ph, lag, blink)));
  if (!got) return undefined;
  frameCacheStats.hits++;
  const [x, y, w, h, res, ax, ay] = got.r;
  const c = canvas(w, h);
  ctx2d(c).drawImage(got.img, x, y, w, h, 0, 0, w, h);
  return { c: atRes(c, res, ax, ay), blades: got.blades };
}

/**
 * Lets go of the loaded pages a while after the loading scene lifts: frames asked for later are
 * drawn by the workers as before (and kept for the next load), and the pages' memory is freed.
 */
export function releaseFrameCacheSoon(): void {
  setTimeout(() => {
    released = true;
    found.clear();
    for (const img of pages.splice(0)) img.close();
  }, RELEASE_MS);
}

// ---------------------------------------------------------------- keeping

/** The page being filled: frames go in shelves, left to right, top to bottom. */
interface Filling {
  c: HTMLCanvasElement;
  g: CanvasRenderingContext2D;
  meta: PageMeta;
  looks: Map<string, number>;
  x: number;
  y: number;
  shelf: number;
}
let filling: Filling | null = null;
/**
 * A stored page's canvas, cleared for the next page: a fresh 2048-square canvas per page (16 MB
 * each, besides the cut copy) was garbage the collector had to come back for.
 */
let spare: { c: HTMLCanvasElement; g: CanvasRenderingContext2D } | null = null;
let flushTimer: ReturnType<typeof setTimeout> | undefined;
/** Frames kept or on their way into a page, so none is kept twice. */
const keptNames = new Set<string>();
let pagesKept = 0;
let counter = 0;

function startKeeping(): void {
  if (typeof document === 'undefined' || typeof indexedDB === 'undefined') return;
  pagesKept = frameCacheStats.pages;
  whenFrameDrawn(keep);
}

function newPage(): Filling {
  const c = spare?.c ?? canvas(PAGE, PAGE);
  const g = spare?.g ?? ctx2d(c);
  spare = null;
  return {
    c,
    g,
    meta: { stamp: stamp(), looks: [], frames: [] },
    looks: new Map(),
    x: 0,
    y: 0,
    shelf: 0,
  };
}

function keep(req: FrameRequest, c: HTMLCanvasElement, blades: BladeLine[]): void {
  if (!ready || released || pagesKept >= MAX_PAGES) return;
  const { width: w, height: h } = c;
  if (!w || !h || w > PAGE || h > PAGE) return;
  const key = frameKey(req.view, req.pose, req.ph, req.lag, req.blink);
  const name = nameOf(req.key, key);
  if (keptNames.has(name) || found.has(name)) return;
  keptNames.add(name);
  let f = (filling ??= newPage());
  // On to the next shelf, or the next page.
  if (f.x + w > PAGE) {
    f.x = 0;
    f.y += f.shelf;
    f.shelf = 0;
  }
  if (f.y + h > PAGE) {
    flush();
    f = filling = newPage();
  }
  f.g.drawImage(c, f.x, f.y);
  let look = f.looks.get(req.key);
  if (look === undefined) {
    look = f.meta.looks.push(req.key) - 1;
    f.looks.set(req.key, look);
  }
  if (blades.length) (f.meta.blades ??= {})[f.meta.frames.length] = blades;
  f.meta.frames.push([look, key, f.x, f.y, w, h, resOf(c), artX(c), artY(c)]);
  f.x += w;
  f.shelf = Math.max(f.shelf, h);
  frameCacheStats.kept++;
  clearTimeout(flushTimer);
  flushTimer = setTimeout(flush, FLUSH_MS);
}

/** Stores the page being filled (cut to its used height) and starts a new one. */
function flush(): void {
  clearTimeout(flushTimer);
  const f = filling;
  filling = null;
  if (!f || !f.meta.frames.length) return;
  pagesKept++;
  const used = Math.min(PAGE, f.y + f.shelf);
  let page = f.c;
  if (used < PAGE) {
    page = canvas(PAGE, used);
    ctx2d(page).drawImage(f.c, 0, 0);
  }
  // Unique across loads and tabs without Math.random (the shot tools seed it).
  const id = `${Date.now().toString(36)}-${(counter++).toString(36)}-${Math.floor(performance.now() * 1000).toString(36)}`;
  const now = f.meta.stamp;
  void (async () => {
    try {
      const png = await new Promise<Blob | null>((ok) => page.toBlob(ok, 'image/png'));
      if (!png) throw new Error('page did not encode');
      const json = new Blob([JSON.stringify(f.meta)], { type: 'application/json' });
      // The picture first: a description without its picture is never found.
      if (!(await assetStore.write(pathOf(id, 'png'), now, png))) throw new Error('page not stored');
      if (!(await assetStore.write(pathOf(id, 'json'), now, json))) {
        void assetStore.remove([pathOf(id, 'png')]);
        throw new Error('page not stored');
      }
      frameCacheStats.stored++;
    } catch {
      frameCacheStats.failed++;
    }
  })();
  // `toBlob` (called above, before the first wait) took its copy of the page: the canvas is free again.
  f.g.clearRect(0, 0, PAGE, PAGE);
  spare = { c: f.c, g: f.g };
}
